import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '../../services/navigation/navigation.service';
import { CacheService } from '../../services/cache/cache.service';
import { CommonService } from '../../services/common/common.service';
import { Column } from '../interfaces/column.interface';
import moment from 'moment';
import { CitiesDataService } from './cities-data.service';
import { City, CitiesResult } from '../interfaces';
import { UsersDataService } from './users-data.service';
import { User, UsersResult } from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class CommentsColumnsService {

  public cities: City[] = [];
  public users: User[] = [];
  public statusesOptions = [{
    value: 'Draft',
    label: 'STATUSESDRAFT',
    css: 'primary'
  }, {
    value: 'Online',
    label: 'STATUSESONLINE',
    css: 'success'
  }, {
    value: 'Deactivated',
    label: 'STATUSESDEACTIVATED',
    css: 'warning'
  }, {
    value: 'Offline',
    label: 'STATUSESOFFLINE',
    css: 'danger'
  }, {
    value: 'Deleted',
    label: 'STATUSESDELETED',
    css: 'default'
  }, {
    value: 'Archived',
    label: 'STATUSESARCHIVED',
    css: 'info'
  }];
  public defaultParameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private cache: CacheService,
    private common: CommonService,
    private citiesDataService: CitiesDataService,
    private usersDataService: UsersDataService,

  ) {
    this.getCities()
    this.getUsers()
  }

  getCities(offset: number = 0): void {
    const cacheKey: string = 'cities.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.cities = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.citiesDataService.getCities(parameters).subscribe((response: CitiesResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.cities = response.results;
        } else {
          this.cities = this.cities.concat(response.results);
        }
        if (response.inlineCount > this.cities.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getCities(offset);
        }
        response.results = this.cities;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getUsers(offset: number = 0): void {
    const cacheKey: string = 'users.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.users = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.usersDataService.getUsers(parameters).subscribe((response: UsersResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.users = response.results;
        } else {
          this.users = this.users.concat(response.results);
        }
        if (response.inlineCount > this.users.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getUsers(offset);
        }
        response.results = this.users;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getColumns(): Column[] {
    return [
      { field: 'id', sortField: 'id', title: this.translate.instant('ID'), show: (this.navigation.getParamValue('comments.id.showFiled', true) && true), displayInList: true, type: 'numericValue'},
      { field: 'entity', sortField: 'entity', title: this.translate.instant('ENTITY'), show: (this.navigation.getParamValue('comments.entity.showFiled', true) && true), displayInList: true, type: 'textValue'},
      { field: 'foreignKey', sortField: 'foreignKey', title: this.translate.instant('FOREIGNKEY'), show: (this.navigation.getParamValue('comments.foreignKey.showFiled', true) && true), displayInList: true, type: 'numericValue'},
      { field: 'pseudo', sortField: 'pseudo', title: this.translate.instant('PSEUDO'), show: (this.navigation.getParamValue('comments.pseudo.showFiled', true) && true), displayInList: true, type: 'textValue'},
      { field: 'age', sortField: 'age', title: this.translate.instant('AGE'), show: (this.navigation.getParamValue('comments.age.showFiled', true) && true), displayInList: true, type: 'numericValue'},
      { field: 'city', title: this.translate.instant('CITY'), type: 'linkValue', filterData: this.cities, show: (this.navigation.getParamValue('comments.cityId.showFiled', true) && true), displayInList: true, filterField: 'city.id', displayField: 'name', sortField: 'city.name', url: 'cities' },
      { field: 'icon', sortField: 'icon', title: this.translate.instant('ICON'), show: (this.navigation.getParamValue('comments.icon.showFiled', true) && true), displayInList: true, type: 'imgValue'},
      { field: 'subject', sortField: 'subject', title: this.translate.instant('SUBJECT'), show: (this.navigation.getParamValue('comments.subject.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'body', sortField: 'body', title: this.translate.instant('BODY'), show: (this.navigation.getParamValue('comments.body.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'status', sortField: 'status', title: this.translate.instant('STATUS'), show: (this.navigation.getParamValue('comments.status.showFiled', false) && (this.common.currentUser.roles.join('').indexOf('ROLE_ADMIN_PUBLISHER') > -1)), displayInList: (this.common.currentUser.roles.join('').indexOf('ROLE_ADMIN_PUBLISHER') > -1), type: 'enumValue', filterData: this.statusesOptions},
      { field: 'createdAt', sortField: 'createdAt', title: this.translate.instant('CREATEDAT'), show: (this.navigation.getParamValue('comments.createdAt.showFiled', false) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'creatorUser', title: this.translate.instant('CREATORUSER'), type: 'linkValue', filterData: this.users, show: (this.navigation.getParamValue('comments.creatorUser.showFiled', false) && true), displayInList: true, filterField: 'creatorUser.id', displayField: 'username', sortField: 'creatorUser.username', url: 'users' },
      { field: 'modifiedAt', sortField: 'modifiedAt', title: this.translate.instant('MODIFIEDAT'), show: (this.navigation.getParamValue('comments.modifiedAt.showFiled', false) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'modifierUser', title: this.translate.instant('MODIFIERUSER'), type: 'linkValue', filterData: this.users, show: (this.navigation.getParamValue('comments.modifierUser.showFiled', false) && true), displayInList: true, filterField: 'modifierUser.id', displayField: 'username', sortField: 'modifierUser.username', url: 'users' },
      { field: 'actions', title: this.translate.instant('ACTIONS'), type: 'static', show: true, displayInList: true, sortField: null},
    ];
  }

  format(fieldName: string, value: any) {
    if (fieldName.toLowerCase().indexOf('time') > -1) {
      return moment(value).format('YYYY-MM-DD HH:mm');
    } else if (fieldName.toLowerCase().indexOf('date') > -1) {
      return moment(value).format('YYYY-MM-DD');
    }
    return value;
  }

}
